import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowForwardRounded, ExpandMoreRounded } from "@mui/icons-material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../layouts/main_layout/main_layout";
import { useFirestore } from "reactfire";
import { useFeedback } from "../../providers/feedback/feedback";
import {
  deaneryList,
  parishListMap,
  ParishOption,
} from "../../utils/deanery_parishes";
import {
  YOUCAT_PAGE_SETTINGS_COLLECTION,
  YOUCAT_PAGE_SETTINGS_DOC_ID,
} from "../admin_portal/youcat/youcat_page_settings_tab";
import { YOUCAT_MONTHLY_MATERIALS_COLLECTION } from "../admin_portal/youcat/youcat_monthly_materials_tab";
import { YOUCAT_MONTHLY_FORM_CONFIGS_COLLECTION } from "../admin_portal/youcat/youcat_monthly_form_tab";

type YoucatPageSettings = {
  description: string;
  icymDexcoInchargeName: string;
  icymDexcoInchargeContact: string;
  ycsDexcoInchargeName: string;
  ycsDexcoInchargeContact: string;
  quizEnabled?: boolean;
  quizTitle?: string;
  quizDescription?: string;
};

type YoucatMonthlyMaterial = {
  docId: string;
  monthKey: string;
  monthLabel: string;
  title: string;
  summary: string;
  coverImage: string;
  materialUrl: string;
  hasSeparateMaterial?: boolean;
};

type YoucatSubmission = {
  docId: string;
  title?: string;
  deanery: string;
  parish: string;
  monthKey: string;
  monthLabel?: string;
  organization: "ICYM" | "YCS";
  youthsParticipatedCount: number;
  imageUrls: string[];
  approvalStatus?: "pending" | "approved" | "rejected";
};

const YOUCAT_BOOK_IMAGE = "/images/youcat/youcat-book.webp";
const YOUCAT_QUERY_ENDPOINT =
  "https://submityoucatquery-f5v5da2yxa-uc.a.run.app";
const DEFAULT_DESCRIPTION = `YOUCAT, the Youth Catechism of the Catholic Church, presents the faith of the Church in a language that speaks clearly to young people. It helps the youth not only to learn what the Church teaches, but also to understand why the faith matters in daily life, prayer, vocation, service, and witness.

For ICYM Udupi Diocese, the YOUCAT initiative strengthens faith formation among young people by encouraging them to study the teachings of the Church with clarity, conviction, and joy. It becomes a means of forming informed, prayerful, and mission-oriented youth who can live and share the Gospel with confidence.

Through this section, the diocese highlights the importance of YOUCAT as a guide for spiritual growth, leadership, and discipleship, while supporting the efforts of ICYM and YCS coordinators who animate this mission at different levels.`;

function getCurrentMonthKey() {
  return new Date().toISOString().slice(0, 7);
}

export default function YoucatPage() {
  const navigate = useNavigate();
  const firestore = useFirestore();
  const { showSnackbar } = useFeedback();
  const [pageSettings, setPageSettings] = useState<YoucatPageSettings>({
    description: DEFAULT_DESCRIPTION,
    icymDexcoInchargeName: "",
    icymDexcoInchargeContact: "",
    ycsDexcoInchargeName: "",
    ycsDexcoInchargeContact: "",
    quizEnabled: false,
    quizTitle: "YouCat Quiz",
    quizDescription:
      "Join the YouCat Quiz through login or signup when the quiz is available.",
  });
  const [materials, setMaterials] = useState<YoucatMonthlyMaterial[]>([]);
  const [submissions, setSubmissions] = useState<YoucatSubmission[]>([]);
  const [selectedMonthKey, setSelectedMonthKey] = useState("");
  const [imageAvailable, setImageAvailable] = useState(true);
  const [activeQuestionCount, setActiveQuestionCount] = useState(0);
  const [isQueryOpen, setIsQueryOpen] = useState(false);
  const [isQuerySaving, setIsQuerySaving] = useState(false);
  const [queryParishOptions, setQueryParishOptions] = useState<ParishOption[]>(
    [],
  );
  const [queryFormValues, setQueryFormValues] = useState({
    name: "",
    deanery: "",
    parish: "",
    phoneNumber: "",
    email: "",
    organization: "" as "ICYM" | "YCS" | "",
    query: "",
  });

  useEffect(() => {
    let isMounted = true;

    async function loadYoucatPage() {
      try {
        const currentMonthKey = getCurrentMonthKey();
        const [
          settingsSnapshot,
          materialsSnapshot,
          submissionsSnapshot,
          formSnapshot,
        ] = await Promise.all([
          getDoc(
            doc(
              firestore,
              YOUCAT_PAGE_SETTINGS_COLLECTION,
              YOUCAT_PAGE_SETTINGS_DOC_ID,
            ),
          ),
          getDocs(
            query(
              collection(firestore, YOUCAT_MONTHLY_MATERIALS_COLLECTION),
              orderBy("monthKey", "desc"),
            ),
          ),
          getDocs(
            query(
              collection(firestore, "youcatMonthlyUpdateSubmissions"),
              orderBy("createdAt", "desc"),
            ),
          ),
          getDoc(
            doc(
              firestore,
              YOUCAT_MONTHLY_FORM_CONFIGS_COLLECTION,
              currentMonthKey,
            ),
          ),
        ]);

        if (!isMounted) {
          return;
        }

        if (settingsSnapshot.exists()) {
          setPageSettings((prev) => ({
            ...prev,
            ...(settingsSnapshot.data() as Partial<YoucatPageSettings>),
          }));
        }

        const monthlyMaterials = materialsSnapshot.docs.map((materialDoc) => ({
          docId: materialDoc.id,
          ...(materialDoc.data() as Omit<YoucatMonthlyMaterial, "docId">),
        }));
        setMaterials(monthlyMaterials);

        const currentMaterial = monthlyMaterials.find(
          (item) => item.monthKey === currentMonthKey,
        );
        setSelectedMonthKey(
          currentMaterial?.monthKey || monthlyMaterials[0]?.monthKey || "",
        );

        setSubmissions(
          submissionsSnapshot.docs.map((updateDoc) => ({
            docId: updateDoc.id,
            ...(updateDoc.data() as Omit<YoucatSubmission, "docId">),
          })),
        );

        setActiveQuestionCount(
          (
            (formSnapshot.data() as { questions?: unknown[] } | undefined)
              ?.questions || []
          ).length,
        );
      } catch (error) {
        console.error("Failed to load YouCat page data", error);
      }
    }

    loadYoucatPage();

    return () => {
      isMounted = false;
    };
  }, [firestore]);

  const selectedMaterial = useMemo(
    () => materials.find((item) => item.monthKey === selectedMonthKey) || null,
    [materials, selectedMonthKey],
  );

  const descriptionParagraphs = useMemo(
    () =>
      pageSettings.description
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean),
    [pageSettings.description],
  );

  const heroMaterialImage =
    selectedMaterial?.coverImage || (imageAvailable ? YOUCAT_BOOK_IMAGE : "");
  const materialActionUrl =
    selectedMaterial?.materialUrl || selectedMaterial?.coverImage || "";

  const gallerySubmissions = useMemo(
    () =>
      submissions.filter(
        (item) => item.approvalStatus === "approved" && item.imageUrls?.length,
      ),
    [submissions],
  );
  const [selectedGallerySubmissionId, setSelectedGallerySubmissionId] =
    useState("");
  const [currentGalleryImageIndex, setCurrentGalleryImageIndex] = useState(0);

  useEffect(() => {
    if (!gallerySubmissions.length) {
      setSelectedGallerySubmissionId("");
      setCurrentGalleryImageIndex(0);
      return;
    }

    setSelectedGallerySubmissionId((prev) => {
      if (
        prev &&
        gallerySubmissions.some((submission) => submission.docId === prev)
      ) {
        return prev;
      }

      return gallerySubmissions[0]?.docId || "";
    });
  }, [gallerySubmissions]);

  const activeGallerySubmission = useMemo(
    () =>
      gallerySubmissions.find(
        (submission) => submission.docId === selectedGallerySubmissionId,
      ) ||
      gallerySubmissions[0] ||
      null,
    [gallerySubmissions, selectedGallerySubmissionId],
  );

  useEffect(() => {
    setCurrentGalleryImageIndex(0);
  }, [activeGallerySubmission?.docId]);

  useEffect(() => {
    const activeImages = activeGallerySubmission?.imageUrls || [];

    if (activeImages.length <= 1) {
      setCurrentGalleryImageIndex(0);
      return;
    }

    const interval = window.setInterval(() => {
      setCurrentGalleryImageIndex((prev) => (prev + 1) % activeImages.length);
    }, 3500);

    return () => window.clearInterval(interval);
  }, [activeGallerySubmission]);

  function validateEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function validatePhone(phone: string) {
    return /^\d{10}$/.test(phone.trim());
  }

  async function handleQuerySubmit() {
    if (
      !queryFormValues.name.trim() ||
      !queryFormValues.deanery.trim() ||
      !queryFormValues.parish.trim() ||
      !queryFormValues.phoneNumber.trim() ||
      !queryFormValues.email.trim() ||
      !queryFormValues.organization ||
      !queryFormValues.query.trim()
    ) {
      showSnackbar("Please fill all query fields");
      return;
    }

    if (!validateEmail(queryFormValues.email)) {
      showSnackbar("Please enter a valid query email address");
      return;
    }

    if (!validatePhone(queryFormValues.phoneNumber)) {
      showSnackbar("Please enter a valid 10 digit phone number");
      return;
    }

    try {
      setIsQuerySaving(true);
      const response = await fetch(YOUCAT_QUERY_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...queryFormValues,
          name: queryFormValues.name.trim(),
          deanery: queryFormValues.deanery.trim(),
          parish: queryFormValues.parish.trim(),
          phoneNumber: queryFormValues.phoneNumber.trim(),
          email: queryFormValues.email.trim(),
          query: queryFormValues.query.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || "Failed to submit query");
      }

      showSnackbar(result?.message || "Your query has been sent successfully");
      setQueryFormValues({
        name: "",
        deanery: "",
        parish: "",
        phoneNumber: "",
        email: "",
        organization: "",
        query: "",
      });
      setQueryParishOptions([]);
      setIsQueryOpen(false);
    } catch (error: any) {
      console.error("Failed to submit YouCat query", error);
      showSnackbar(error?.message || "Failed to submit query");
    } finally {
      setIsQuerySaving(false);
    }
  }

  return (
    <MainLayout>
      <Box my={{ xs: 2, md: 4 }}>
        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="stretch">
          <Grid item xs={12} lg={7}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                p: { xs: 2.5, md: 3 },
                background:
                  "linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,255,255,1) 100%)",
                border: "1px solid rgba(217,119,6,0.14)",
                boxShadow: "0 24px 48px rgba(180, 120, 20, 0.12)",
              }}
            >
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  p: 0,
                  boxShadow: "none",
                  bgcolor: "transparent",
                  border: "none",
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Chip
                      label="Youth Catechism"
                      sx={{
                        bgcolor: "#fff3c4",
                        color: "#8a5a00",
                        fontWeight: 800,
                        mb: 1,
                      }}
                    />
                    <Typography
                      variant="h3"
                      fontWeight={900}
                      sx={{ color: "#d97706", letterSpacing: "-0.03em" }}
                    >
                      YOUCAT
                    </Typography>
                    <Typography color="#8a6a17" mt={1}>
                      Faith formation for the youth of ICYM Udupi Diocese
                    </Typography>
                  </Box>
                  <Typography
                    variant="overline"
                    sx={{ color: "#a16207", fontWeight: 800 }}
                  >
                    Why YOUCAT Matters
                  </Typography>
                  {descriptionParagraphs.map((paragraph, index) => (
                    <Typography
                      key={index}
                      sx={{
                        color: "#5b4a21",
                        lineHeight: 1.85,
                        fontSize: { xs: "0.98rem", md: "1rem" },
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Stack>
              </Card>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                p: { xs: 2.5, md: 3 },
                background:
                  "linear-gradient(180deg, rgba(255,244,199,1) 0%, rgba(255,255,255,1) 100%)",
                border: "1px solid rgba(217,119,6,0.16)",
                boxShadow: "0 24px 48px rgba(180, 120, 20, 0.12)",
              }}
            >
              <Stack spacing={2} sx={{ height: "100%" }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                >
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: "#a16207", fontWeight: 800 }}
                    >
                      Monthly YouCat Material
                    </Typography>
                    <Typography variant="h5" fontWeight={900}>
                      {selectedMaterial?.monthLabel || "Current Material"}
                    </Typography>
                  </Box>
                  <TextField
                    select
                    size="small"
                    label="Month"
                    value={selectedMonthKey}
                    onChange={(event) =>
                      setSelectedMonthKey(event.target.value)
                    }
                    sx={{ minWidth: { xs: "100%", sm: 220 } }}
                  >
                    {materials.map((material) => (
                      <MenuItem key={material.docId} value={material.monthKey}>
                        {material.monthLabel}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {heroMaterialImage ? (
                  <Box
                    component="img"
                    src={heroMaterialImage}
                    alt={selectedMaterial?.title || "YOUCAT monthly material"}
                    onError={() => setImageAvailable(false)}
                    sx={{
                      width: "100%",
                      flex: 1,
                      minHeight: { xs: 280, sm: 360, lg: 420 },
                      maxHeight: { xs: 360, sm: 420, lg: 520 },
                      borderRadius: 3,
                      objectFit: "contain",
                      bgcolor: "#fff8db",
                      border: "1px solid rgba(217,119,6,0.16)",
                      p: 1.5,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: { xs: 280, sm: 360, lg: 420 },
                      borderRadius: 3,
                      border: "1px dashed rgba(217,119,6,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      px: 3,
                      color: "#8a6a17",
                    }}
                  >
                    Monthly material cover will appear here.
                  </Box>
                )}

                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    {selectedMaterial?.title || "YOUCAT Book"}
                  </Typography>
                  <Typography color="#7c651d" mt={1}>
                    {selectedMaterial?.summary || "YouCat - Youth Catechism"}
                  </Typography>
                </Box>

                <Box display="flex" gap={1} flexWrap="wrap">
                  {materials.map((material) => (
                    <Button
                      key={material.docId}
                      size="small"
                      variant={
                        material.monthKey === selectedMonthKey
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => setSelectedMonthKey(material.monthKey)}
                      sx={
                        material.monthKey === selectedMonthKey
                          ? {
                              bgcolor: "#d97706",
                              "&:hover": {
                                bgcolor: "#b45309",
                              },
                            }
                          : {
                              color: "#a16207",
                              borderColor: "rgba(217,119,6,0.3)",
                            }
                      }
                    >
                      {material.monthLabel}
                    </Button>
                  ))}
                </Box>

                {materialActionUrl ? (
                  <Button
                    variant="contained"
                    href={materialActionUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {selectedMaterial?.hasSeparateMaterial
                      ? "Open Material File"
                      : "View Material Image"}
                  </Button>
                ) : null}

                <Card
                  sx={{
                    borderRadius: 3,
                    p: 2.5,
                    bgcolor: "#fff8db",
                    border: "1px solid rgba(217,119,6,0.14)",
                    boxShadow: "none",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: "#a16207", fontWeight: 800 }}
                  >
                    Monthly Updates Form
                  </Typography>
                  <Typography variant="h6" fontWeight={800} mt={1}>
                    Submit this month&apos;s YouCat unit update
                  </Typography>
                  <Typography color="#7c651d" mt={1} mb={2}>
                    Open the form and share your monthly material follow-up from
                    the unit.
                  </Typography>
                  <Typography color="#7c651d" mb={2}>
                    {activeQuestionCount
                      ? `${activeQuestionCount} question${
                          activeQuestionCount === 1 ? "" : "s"
                        } ${
                          activeQuestionCount === 1 ? "is" : "are"
                        } active for this month.`
                      : "The form becomes available automatically when this month's questions are added by the admin."}
                  </Typography>
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardRounded />}
                    onClick={() => navigate("/youcat/monthly-update")}
                    disabled={!activeQuestionCount}
                  >
                    Open Monthly Update Form
                  </Button>
                </Card>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={{ xs: 0.5, md: 1 }}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                p: 2,
                bgcolor: "#fff8db",
                border: "1px solid rgba(217,119,6,0.14)",
              }}
            >
              <Typography
                variant="overline"
                sx={{ color: "#a16207", fontWeight: 800 }}
              >
                ICYM DEXCO YouCat In-charge
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} mt={0.75}>
                {pageSettings.icymDexcoInchargeName || "To be updated"}
              </Typography>
              {pageSettings.icymDexcoInchargeContact ? (
                <Typography color="#7c651d" mt={0.5} variant="body2">
                  Contact: {pageSettings.icymDexcoInchargeContact}
                </Typography>
              ) : null}
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                p: 2,
                bgcolor: "#fffdf0",
                border: "1px solid rgba(217,119,6,0.14)",
              }}
            >
              <Typography
                variant="overline"
                sx={{ color: "#a16207", fontWeight: 800 }}
              >
                YCS DEXCO YouCat In-charge
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} mt={0.75}>
                {pageSettings.ycsDexcoInchargeName || "To be updated"}
              </Typography>
              {pageSettings.ycsDexcoInchargeContact ? (
                <Typography color="#7c651d" mt={0.5} variant="body2">
                  Contact: {pageSettings.ycsDexcoInchargeContact}
                </Typography>
              ) : null}
            </Card>
          </Grid>
        </Grid>
        <Box mt={{ xs: 4, md: 5 }}>
          {pageSettings.quizEnabled ? (
            <Card
              sx={{
                borderRadius: 4,
                p: { xs: 2.5, md: 3 },
                bgcolor: "#fff8db",
                border: "1px solid rgba(217,119,6,0.14)",
                boxShadow: "0 18px 36px rgba(180, 120, 20, 0.08)",
                mb: 4,
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                flexDirection={{ xs: "column", md: "row" }}
                gap={2}
              >
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: "#a16207", fontWeight: 800 }}
                  >
                    YouCat Quiz
                  </Typography>
                  <Typography variant="h5" fontWeight={900} color="#5c4100">
                    {pageSettings.quizTitle || "YouCat Quiz"}
                  </Typography>
                  <Typography color="#7c651d" mt={1}>
                    {pageSettings.quizDescription ||
                      "Login or signup to access the quiz section when the quiz is published."}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardRounded />}
                  onClick={() => navigate("/youcat/quiz")}
                >
                  Open Quiz
                </Button>
              </Box>
            </Card>
          ) : null}
          <Box mt={{ xs: 4, md: 5 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={1}
              mb={2}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "#a16207", fontWeight: 800 }}
                >
                  Unit Updates
                </Typography>
                <Typography variant="h4" fontWeight={900} color="#5c4100">
                  Monthly YouCat Updates Gallery
                </Typography>
              </Box>
              <Typography color="#7c651d">
                Latest updates from YouCat units across the diocese
              </Typography>
            </Box>

            {!gallerySubmissions.length ? (
              <Card
                sx={{
                  borderRadius: 4,
                  p: 3,
                  bgcolor: "#fffdf0",
                  border: "1px solid rgba(217,119,6,0.12)",
                }}
              >
                <Typography variant="h6" fontWeight={800}>
                  No unit updates yet
                </Typography>
                <Typography color="#7c651d" mt={1}>
                  Approved unit-level YouCat image updates will appear here
                  after admin review.
                </Typography>
              </Card>
            ) : (
              <Card
                sx={{
                  borderRadius: 4,
                  p: { xs: 2, md: 3 },
                  background:
                    "linear-gradient(180deg, rgba(255,248,220,1) 0%, rgba(255,255,255,1) 100%)",
                  border: "1px solid rgba(217,119,6,0.12)",
                  boxShadow: "0 18px 36px rgba(180, 120, 20, 0.1)",
                }}
              >
                <Stack spacing={2.5}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {gallerySubmissions.map((submission) => (
                      <Button
                        key={submission.docId}
                        size="small"
                        variant={
                          submission.docId === activeGallerySubmission?.docId
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() =>
                          setSelectedGallerySubmissionId(submission.docId)
                        }
                        sx={
                          submission.docId === activeGallerySubmission?.docId
                            ? {
                                bgcolor: "#d97706",
                                "&:hover": { bgcolor: "#b45309" },
                              }
                            : {
                                color: "#a16207",
                                borderColor: "rgba(217,119,6,0.3)",
                              }
                        }
                      >
                        {submission.organization} - {submission.parish}
                      </Button>
                    ))}
                  </Box>

                  {activeGallerySubmission ? (
                    <>
                      <Box
                        sx={{
                          position: "relative",
                          borderRadius: 3,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src={
                            activeGallerySubmission.imageUrls[
                              currentGalleryImageIndex
                            ] || ""
                          }
                          alt={`${activeGallerySubmission.parish} update ${currentGalleryImageIndex + 1}`}
                          sx={{
                            width: "100%",
                            height: "auto",
                            maxHeight: { xs: 360, sm: 420, md: 520 },
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </Box>

                      {activeGallerySubmission.imageUrls.length > 1 ? (
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          {activeGallerySubmission.imageUrls.map((_, index) => (
                            <Box
                              key={index}
                              onClick={() => setCurrentGalleryImageIndex(index)}
                              sx={{
                                width:
                                  index === currentGalleryImageIndex ? 28 : 10,
                                height: 10,
                                borderRadius: "999px",
                                bgcolor:
                                  index === currentGalleryImageIndex
                                    ? "#d97706"
                                    : "rgba(217,119,6,0.22)",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                            />
                          ))}
                        </Stack>
                      ) : null}

                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: "#a16207", fontWeight: 800 }}
                        >
                          {activeGallerySubmission.organization} Unit Update
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                          {activeGallerySubmission.parish}
                        </Typography>
                        <Typography color="#7c651d" mt={0.5}>
                          {activeGallerySubmission.deanery} Deanery
                        </Typography>
                        
                      </Box>
                    </>
                  ) : null}
                </Stack>
              </Card>
            )}
          </Box>

          <Card
            sx={{
              borderRadius: 4,
              p: { xs: 2.5, md: 3 },
              bgcolor: "#fffdf0",
              border: "1px solid rgba(217,119,6,0.14)",
              boxShadow: "0 18px 36px rgba(180, 120, 20, 0.08)",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "#a16207", fontWeight: 800 }}
                >
                  Ask Any Queries
                </Typography>
                <Typography variant="h5" fontWeight={900} color="#5c4100">
                  Need help with YouCat?
                </Typography>
                <Typography color="#7c651d" mt={1}>
                  Send your query to the YouCat Incharges and Council.
                </Typography>
              </Box>
              <Button
                variant="contained"
                endIcon={<ExpandMoreRounded />}
                onClick={() => setIsQueryOpen((prev) => !prev)}
              >
                {isQueryOpen ? "Hide Query Form" : "Open Query Form"}
              </Button>
            </Box>

            <Collapse in={isQueryOpen}>
              <Grid container spacing={3} mt={0.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Name"
                    value={queryFormValues.name}
                    onChange={(event) =>
                      setQueryFormValues((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Unit ICYM or YCS"
                    value={queryFormValues.organization}
                    onChange={(event) =>
                      setQueryFormValues((prev) => ({
                        ...prev,
                        organization: event.target.value as "ICYM" | "YCS",
                      }))
                    }
                  >
                    <MenuItem value="ICYM">ICYM</MenuItem>
                    <MenuItem value="YCS">YCS</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={deaneryList}
                    value={
                      deaneryList.find(
                        (item) => item.name === queryFormValues.deanery,
                      ) || null
                    }
                    onChange={(_event, value) => {
                      setQueryFormValues((prev) => ({
                        ...prev,
                        deanery: value?.name || "",
                        parish: "",
                      }));
                      setQueryParishOptions(
                        value ? parishListMap[value.name] || [] : [],
                      );
                    }}
                    getOptionLabel={(option) => option.name || ""}
                    renderInput={(params) => (
                      <TextField {...params} required label="Deanery" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={queryParishOptions}
                    value={
                      queryParishOptions.find(
                        (item) => item.value === queryFormValues.parish,
                      ) || null
                    }
                    onChange={(_event, value) =>
                      setQueryFormValues((prev) => ({
                        ...prev,
                        parish: value?.value || "",
                      }))
                    }
                    getOptionLabel={(option) => option.value || ""}
                    renderInput={(params) => (
                      <TextField {...params} required label="Parish" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone Number"
                    value={queryFormValues.phoneNumber}
                    onChange={(event) =>
                      setQueryFormValues((prev) => ({
                        ...prev,
                        phoneNumber: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    value={queryFormValues.email}
                    onChange={(event) =>
                      setQueryFormValues((prev) => ({
                        ...prev,
                        email: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={5}
                    label="Your Query"
                    value={queryFormValues.query}
                    onChange={(event) =>
                      setQueryFormValues((prev) => ({
                        ...prev,
                        query: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleQuerySubmit}
                    disabled={isQuerySaving}
                  >
                    {isQuerySaving ? "Sending..." : "Send Query"}
                  </Button>
                </Grid>
              </Grid>
            </Collapse>
          </Card>
        </Box>
      </Box>
    </MainLayout>
  );
}
